import { Fragment, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button, useToast } from '../../components/primitives';
import { SendEmailModal } from '../../components/SendEmailModal';
import { buildDocHtml } from '../../lib/docHtml';
import { printToPdf } from '../../lib/printToPdf';
import { useDocEditor } from '../../lib/useDocEditor';
import { useDocDraft } from '../../lib/useDocDraft';
import { docSaveErrorMessage } from '../../lib/docSaveError';
import { useSaveDocument } from '../../api/hooks';
import { useActiveStavba } from '../stavba-context/StavbaContext';
import { czAccountToIban, buildSpayd } from '../../lib/iban';
import { blobToBase64 } from '../../lib/docBundle';
import { useInvoiceBundle, InvoiceBundlePanel } from './InvoiceBundle';
import {
  DocLayout,
  DocPage,
  DocBlock,
  DocRow,
  DocSignatures,
  DocContactQR,
  DocInlineDate,
  DocInlineAresName,
  DocInlineAresLookup,
  DocActionButton,
  DocItemsTable,
  DocDphSelect,
  type DocItem,
  SavedDocsButton,
  NewDocButton,
  DraftBanner,
  useDocChain,
  useDocTransferIn,
  DocChainBar,
  useDocRows,
  DocCustomRows,
  DocAddRowButton,
} from '../../components/document';
import { MapPickerButton } from '../../components/map';
import {
  CompanyPickerButton,
  SaveCompanyButton,
  type AresPickResult,
  type CompanyPickResult,
} from '../../components/ares';
import {
  StavbaPickerButton,
  SaveStavbaButton,
  type StavbaPickResult,
} from '../../components/stavba';

/**
 * Faktura — „živý dokument".
 * Sekce: Údaje faktury / Stavba / Dodavatel / Bankovní spojení / Odběratel /
 * Položky / Podpisy. QR platba (SPD) se vykresluje uvnitř sekce „Bankovní spojení".
 */

function deriveInitials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => w && /[\p{L}]/u.test(w[0]) && !/^(s\.?r\.?o\.?|a\.?s\.?|spol|ltd|inc)$/i.test(w));
  if (words.length === 0) return '—';
  return words
    .slice(0, 3)
    .map((w) => w[0].toLocaleUpperCase('cs'))
    .join('');
}

function newId(prefix: string): string {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
}

function parseCzechNumber(str: string): number {
  return Number((str ?? '').replace(/\s/g, '').replace(',', '.'));
}

function formatCzechNumber(n: number): string {
  return n.toLocaleString('cs-CZ').replace(/,/g, ' ');
}

/** Parsuje datum ve tvaru „dd. mm. rrrr" (i bez mezer). */
function parseCzDate(s: string): Date | null {
  const m = (s ?? '').match(/(\d{1,2})\D+(\d{1,2})\D+(\d{4})/);
  if (!m) return null;
  return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]), 12, 0);
}

function fmtCzDate(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}. ${p(d.getMonth() + 1)}. ${d.getFullYear()}`;
}

/** Vrátí datum vystavení + dny ve tvaru „dd. mm. rrrr", nebo null při neplatném vstupu. */
function addDays(dateStr: string, days: number): string | null {
  const d = parseCzDate(dateStr);
  if (!d) return null;
  d.setDate(d.getDate() + days);
  return fmtCzDate(d);
}

const initialItems: DocItem[] = [
  { id: '1', desc: '', qty: '', unit: '', price: '', total: '' },
];

export function InvoicePage() {
  const saveDoc = useSaveDocument();
  const toast = useToast();
  const { activeStavba } = useActiveStavba();
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);

  const [meta, setMeta] = useState({
    cislo: '',
    vs: '',
    ss: '',
    datum: '',
    splatnostDny: '35',
    splatnost: '',
    duzp: '',
    forma: 'Bankovní převod',
  });
  const [stavba, setStavba] = useState({
    nazev: '',
    iniciály: '',
    adresa: '',
    cPovoleni: '',
  });
  const [dod, setDod] = useState({
    nazev: '',
    sidlo: '',
    ico: '',
    dic: '',
    tel: '',
    email: '',
    ucet: '',
    iban: '',
    bic: '',
  });
  const [obj, setObj] = useState({
    nazev: '',
    sidlo: '',
    ico: '',
    dic: '',
    tel: '',
    email: '',
  });
  const [items, setItems] = useState<DocItem[]>(initialItems);
  const [dphPct, setDphPct] = useState('21');
  const [ibanError, setIbanError] = useState<string | null>(null);

  const [poznamky, setPoznamky] = useState<Array<{ id: string; text: string }>>([
    { id: newId('pzn'), text: 'Fakturujeme Vám provedené práce dle soupisu a smlouvy o dílo.' },
  ]);
  const addPoznamka = () =>
    setPoznamky((list) => [...list, { id: newId('pzn'), text: '' }]);
  const updatePoznamka = (id: string, text: string) =>
    setPoznamky((list) => list.map((p) => (p.id === id ? { ...p, text } : p)));
  const removePoznamka = (id: string) =>
    setPoznamky((list) => list.filter((p) => p.id !== id));

  // Datum splatnosti = datum vystavení + počet dní (auto-dopočet, lze přepsat ručně).
  const onDatumVystaveni = (v: string) =>
    setMeta((m) => {
      const r = addDays(v, parseInt(m.splatnostDny) || 0);
      return { ...m, datum: v, splatnost: r ?? m.splatnost };
    });
  const onSplatnostDny = (v: string) =>
    setMeta((m) => {
      const r = addDays(m.datum, parseInt(v) || 0);
      return { ...m, splatnostDny: v, splatnost: r ?? m.splatnost };
    });

  const [podpisy, setPodpisy] = useState({
    vystavil: { jmeno: '', funkce: '', image: '' },
    prevzal: { jmeno: '', funkce: '', image: '' },
    extra: [] as Array<{ id: string; strana: string; jmeno: string; funkce: string; image: string }>,
  });

  // Skryté řádky + vlastní řádky v hlavičkových sekcích.
  const chain = useDocChain();
  const rows = useDocRows();
  const { isHidden, hide, restoreSection, hiddenCount } = rows;

  const { subtotal, vat, total } = useMemo(() => {
    const sub = items.reduce((acc, it) => acc + parseCzechNumber(it.total), 0);
    const rate = (parseFloat(dphPct) || 0) / 100;
    const vatAmount = Math.round(sub * rate);
    const tot = Math.round(sub + vatAmount);
    return { subtotal: sub, vat: vatAmount, total: tot };
  }, [items, dphPct]);

  const spd = useMemo(() => {
    const iban = dod.iban.replace(/\s/g, '');
    if (!iban) return '';
    return buildSpayd({
      iban,
      amount: total,
      vs: meta.vs,
      msg: meta.cislo ? `Faktura ${meta.cislo}` : undefined,
    });
  }, [dod.iban, total, meta.vs, meta.cislo]);

  function handleGenerateIban() {
    const ucet = dod.ucet.trim();
    if (!ucet) {
      setIbanError('Nejprve zadejte číslo účtu ve formátu: předčíslí-účet/kód nebo účet/kód');
      return;
    }
    const iban = czAccountToIban(ucet);
    if (!iban) {
      setIbanError('Neplatný formát. Např.: 19-2000145399/0800 nebo 123456789/0300');
      return;
    }
    setIbanError(null);
    setDod((d) => ({ ...d, iban }));
  }

  /** Předvyplní formulář ze snímku dat (dokument z archivu i lokální koncept). */
  function applySnapshot(d: Record<string, unknown>) {
    if (d.meta) setMeta((m) => ({ ...m, ...(d.meta as Partial<typeof meta>) }));
    if (d.stavba) setStavba((s) => ({ ...s, ...(d.stavba as Partial<typeof stavba>) }));
    if (d.dod) setDod((x) => ({ ...x, ...(d.dod as Partial<typeof dod>) }));
    if (d.obj) setObj((x) => ({ ...x, ...(d.obj as Partial<typeof obj>) }));
    if (Array.isArray(d.items)) setItems(d.items as DocItem[]);
    if (d.dphPct != null) setDphPct(String(d.dphPct));
    if (Array.isArray(d.poznamky)) setPoznamky(d.poznamky as Array<{ id: string; text: string }>);
    if (d.podpisy) setPodpisy((p) => ({ ...p, ...(d.podpisy as Partial<typeof podpisy>) }));
    chain.apply(d);
    rows.apply(d.radky);
  }

  const docData = {
    meta, stavba, dod, obj, items, dphPct, subtotal, vat, total, spd, poznamky, podpisy,
    radky: rows.snapshot(),
    zdroj: chain.zdroj,
  };
  const { editId } = useDocEditor(applySnapshot);
  useDocTransferIn('faktura', applySnapshot, editId);
  // Přílohy z řetězce dokladů (soupis, ZP, předávací protokol).
  const bundle = useInvoiceBundle(chain.zdroj);
  const withBundle = !!chain.zdroj && bundle.chosen.length > 0;

  // Autosave rozpracovaného konceptu do prohlížeče (jen u nového dokumentu) —
  // bez něj se při zavření/reloadu záložky ztratil celý rozepsaný formulář.
  const draft = useDocDraft<Record<string, unknown>>('faktura', editId == null);
  function restoreDraft() {
    if (!draft.pending) return;
    applySnapshot(draft.pending.data);
    draft.accept();
  }
  useEffect(() => {
    draft.persist(docData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, stavba, dod, obj, items, dphPct, poznamky, podpisy, rows, chain.zdroj]);

  /**
   * Uložení do archivu. Chybu je nutné ukázat — dřív se `mutateAsync` odmítlo
   * bez ošetření a uživatel si myslel, že je dokument uložený.
   */
  async function handleSave(): Promise<number | null> {
    try {
      const res = await saveDoc.mutateAsync({
        id: editId ?? chain.savedId ?? undefined,
        doc_type: 'faktura',
        title: docTitle,
        stavba: activeStavba?.nazev ?? '',
        data_json: docData,
      });
      draft.clear();
      toast.success('Faktura uložena do archivu.');
      chain.markSaved(res.id);
      return res.id;
    } catch (e) {
      toast.error(docSaveErrorMessage(e));
      return null;
    }
  }

  const setMetaPatch = (patch: Partial<typeof meta>) => setMeta((m) => ({ ...m, ...patch }));
  const setDodPatch = (patch: Partial<typeof dod>) => setDod((d) => ({ ...d, ...patch }));
  const setObjPatch = (patch: Partial<typeof obj>) => setObj((o) => ({ ...o, ...patch }));
  const setStavbaPatch = (patch: Partial<typeof stavba>) => setStavba((s) => ({ ...s, ...patch }));

  const addExtra = () =>
    setPodpisy((p) => ({
      ...p,
      extra: [
        ...p.extra,
        { id: newId('ex'), strana: 'Účastník', jmeno: '', funkce: '', image: '' },
      ],
    }));
  const updateExtra = (id: string, patch: Partial<{ strana: string; jmeno: string; funkce: string; image: string }>) =>
    setPodpisy((p) => ({
      ...p,
      extra: p.extra.map((x) => (x.id === id ? { ...x, ...patch } : x)),
    }));
  const removeExtra = (id: string) =>
    setPodpisy((p) => ({ ...p, extra: p.extra.filter((x) => x.id !== id) }));

  const fmtKc = (n: number) => `${formatCzechNumber(n)} Kč`;

  const docTitle = meta.cislo ? `Faktura ${meta.cislo}` : 'Faktura';
  const metaLine = [meta.datum, meta.splatnost && `splatnost ${meta.splatnost}`]
    .filter(Boolean)
    .join(' · ');

  return (
    <>
      <DocLayout
        breadcrumb={['Faktury', `#${meta.cislo || '—'}`]}
        title={docTitle}
        statusText="Rozpracovaný koncept"
        status="draft"
        actions={
          <>
            <NewDocButton draftKey="faktura" path="/app/faktura" />
            <SavedDocsButton
              docType="faktura"
              title="Uložené faktury"
              currentId={editId}
            />
            <Button variant="secondary" leading="⎙" onClick={() => printToPdf(`Faktura_${meta.cislo || 'koncept'}.pdf`)}>PDF</Button>
            <Button variant="secondary" leading="📧" onClick={() => setEmailOpen(true)}>
              Odeslat e-mailem
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={saveDoc.isPending}>
              {saveDoc.isPending ? 'Ukládám…' : saveDoc.isSuccess ? 'Uloženo ✓' : 'Uložit'}
            </Button>
          </>
        }
      >
        <DocChainBar zdroj={chain.zdroj} />
        {chain.zdroj && (
          <InvoiceBundlePanel
            bundle={bundle}
            buildInvoiceHtml={() => buildDocHtml(previewRef.current)}
            invoiceTitle={docTitle}
            onSignInvoice={(sig) =>
              setPodpisy((p) => ({ ...p, vystavil: { ...p.vystavil, image: sig } }))
            }
          />
        )}
        {draft.pending && (
          <DraftBanner
            savedAt={draft.pending.savedAt}
            onRestore={restoreDraft}
            onDiscard={draft.clear}
          />
        )}
        <div ref={previewRef}>
          <DocPage
            company={{
              initials: deriveInitials(dod.nazev),
              name: dod.nazev || '—',
              address: `${dod.sidlo || '—'}${dod.ico ? ' · IČ ' + dod.ico : ''}`,
            }}
            tag="Faktura — daňový doklad"
            title={meta.cislo ? `Faktura č. ${meta.cislo}` : 'Faktura'}
            meta={metaLine || undefined}
            footer={{ left: dod.nazev || '—', center: 'str. 1 / 1', right: meta.cislo || '—' }}
          >
            <DocBlock
              idx="01"
              title="Údaje faktury"
              hiddenCount={hiddenCount('s01')}
              onRestoreHidden={() => restoreSection('s01')}
              actions={<DocAddRowButton rows={rows} section="s01" />}
            >
              {!isHidden('s01.cislo') && (
                <DocRow
                  k="Číslo faktury"
                  mono
                  value={meta.cislo}
                  onChange={(v) => setMetaPatch({ cislo: v })}
                  placeholder="např. 2026-041"
                  onRemove={() => hide('s01.cislo')}
                />
              )}
              {!isHidden('s01.vs') && (
                <DocRow
                  k="Variabilní symbol"
                  mono
                  value={meta.vs}
                  onChange={(v) => setMetaPatch({ vs: v })}
                  placeholder="např. 2026041"
                  onRemove={() => hide('s01.vs')}
                />
              )}
              {!isHidden('s01.datum') && (
                <DocRow
                  k="Datum vystavení"
                  mono
                  value={meta.datum}
                  onChange={onDatumVystaveni}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={meta.datum} onChange={onDatumVystaveni} />}
                  onRemove={() => hide('s01.datum')}
                />
              )}
              {!isHidden('s01.splatnostDny') && (
                <DocRow
                  k="Splatnost (dny)"
                  mono
                  value={meta.splatnostDny}
                  onChange={onSplatnostDny}
                  placeholder="35"
                  onRemove={() => hide('s01.splatnostDny')}
                />
              )}
              {!isHidden('s01.splatnost') && (
                <DocRow
                  k="Datum splatnosti"
                  mono
                  value={meta.splatnost}
                  onChange={(v) => setMetaPatch({ splatnost: v })}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={meta.splatnost} onChange={(v) => setMetaPatch({ splatnost: v })} />}
                  onRemove={() => hide('s01.splatnost')}
                />
              )}
              {!isHidden('s01.duzp') && (
                <DocRow
                  k="DUZP"
                  mono
                  value={meta.duzp}
                  onChange={(v) => setMetaPatch({ duzp: v })}
                  placeholder="dd. mm. rrrr"
                  actions={<DocInlineDate value={meta.duzp} onChange={(v) => setMetaPatch({ duzp: v })} />}
                  onRemove={() => hide('s01.duzp')}
                />
              )}
              {!isHidden('s01.forma') && (
                <DocRow
                  k="Forma úhrady"
                  value={meta.forma}
                  onChange={(v) => setMetaPatch({ forma: v })}
                  onRemove={() => hide('s01.forma')}
                />
              )}
              <DocCustomRows rows={rows} section="s01" />
            </DocBlock>

            <DocBlock
              idx="02"
              title="Stavba"
              hiddenCount={hiddenCount('s02')}
              onRestoreHidden={() => restoreSection('s02')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s02" />
                  <StavbaPickerButton
                    title="Načíst stavbu ze seznamu"
                    onPick={(p: StavbaPickResult) =>
                      setStavba((s) => ({
                        ...s,
                        nazev: p.nazev,
                        iniciály: deriveInitials(p.nazev),
                        adresa: p.adresa,
                        cPovoleni: p.stavebni_povoleni,
                      }))
                    }
                  />
                  <SaveStavbaButton
                    title="Uložit stavbu"
                    build={() => ({
                      nazev: stavba.nazev,
                      adresa: stavba.adresa,
                      stavebni_povoleni: stavba.cPovoleni,
                      zakazka_cislo: '',
                      datum_zahajeni: '',
                      datum_ukonceni: '',
                    })}
                  />
                </>
              }
            >
              {!isHidden('s02.nazev') && (
                <DocRow
                  k="Název stavby"
                  value={stavba.nazev}
                  onChange={(v) => setStavbaPatch({ nazev: v })}
                  placeholder="(volitelné — k jaké zakázce se faktura vztahuje)"
                  onRemove={() => hide('s02.nazev')}
                />
              )}
              {!isHidden('s02.adresa') && (
                <DocRow
                  k="Adresa stavby"
                  value={stavba.adresa}
                  onChange={(v) => setStavbaPatch({ adresa: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={stavba.adresa}
                      onApply={(v) => setStavbaPatch({ adresa: v })}
                      title="Adresa stavby"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s02.adresa')}
                />
              )}
              {!isHidden('s02.povoleni') && (
                <DocRow
                  k="Stavební povolení"
                  value={stavba.cPovoleni}
                  onChange={(v) => setStavbaPatch({ cPovoleni: v })}
                  placeholder="např. MÚ-2026/0341"
                  onRemove={() => hide('s02.povoleni')}
                />
              )}
              <DocCustomRows rows={rows} section="s02" />
            </DocBlock>

            <DocBlock
              idx="03"
              title="Dodavatel"
              hiddenCount={hiddenCount('s03')}
              onRestoreHidden={() => restoreSection('s03')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s03" />
                  <CompanyPickerButton
                    preferRole="zhotovitel"
                    title="Načíst dodavatele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setDod((d) => ({
                        ...d,
                        nazev: c.nazev,
                        sidlo: c.adresa,
                        ico: c.ico,
                        dic: c.dic,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      }))
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit dodavatele do adresáře"
                    build={() => ({
                      role: 'zhotovitel',
                      nazev: dod.nazev,
                      ico: dod.ico,
                      dic: dod.dic,
                      adresa: dod.sidlo,
                      kontakt_tel: dod.tel,
                      kontakt_email: dod.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s03.nazev') && (
                <DocRow
                  k="Název"
                  v={
                    <DocInlineAresName
                      value={dod.nazev}
                      placeholder="např. Stavebniny Novák s.r.o."
                      onChange={(v) => setDodPatch({ nazev: v })}
                      onPick={(d: AresPickResult) =>
                        setDodPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s03.nazev')}
                />
              )}
              {!isHidden('s03.sidlo') && (
                <DocRow
                  k="Sídlo"
                  value={dod.sidlo}
                  onChange={(v) => setDodPatch({ sidlo: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={dod.sidlo}
                      onApply={(v) => setDodPatch({ sidlo: v })}
                      title="Sídlo dodavatele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s03.sidlo')}
                />
              )}
              {!isHidden('s03.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={dod.ico}
                  onChange={(v) => setDodPatch({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={dod.ico}
                      onResult={(d) => setDodPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })}
                    />
                  }
                  onRemove={() => hide('s03.ico')}
                />
              )}
              {!isHidden('s03.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={dod.dic}
                  onChange={(v) => setDodPatch({ dic: v })}
                  placeholder="CZ12345678"
                  onRemove={() => hide('s03.dic')}
                />
              )}
              {!isHidden('s03.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={dod.tel}
                  onChange={(v) => setDodPatch({ tel: v })}
                  placeholder="+420 …"
                  inputType="tel"
                  onRemove={() => hide('s03.tel')}
                />
              )}
              {!isHidden('s03.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={dod.email}
                  onChange={(v) => setDodPatch({ email: v })}
                  placeholder="email@…"
                  inputType="email"
                  actions={
                    <DocContactQR
                      name={dod.nazev}
                      role="Dodavatel"
                      phone={dod.tel}
                      email={dod.email}
                      title="QR vizitka — dodavatel"
                    />
                  }
                  onRemove={() => hide('s03.email')}
                />
              )}
              <DocCustomRows rows={rows} section="s03" />
            </DocBlock>

            <DocBlock
              idx="04"
              title="Bankovní spojení"
              hiddenCount={hiddenCount('s04')}
              onRestoreHidden={() => restoreSection('s04')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s04" />
                  <DocActionButton onClick={handleGenerateIban} title="Dopočítat IBAN z čísla účtu (mod97) a vygenerovat QR">
                    IBAN + QR
                  </DocActionButton>
                </>
              }
            >
              {!isHidden('s04.ucet') && (
                <DocRow
                  k="Číslo účtu"
                  mono
                  value={dod.ucet}
                  onChange={(v) => setDodPatch({ ucet: v })}
                  placeholder="19-2000145399/0800"
                  onRemove={() => hide('s04.ucet')}
                />
              )}
              {!isHidden('s04.iban') && (
                <DocRow
                  k="IBAN"
                  mono
                  value={dod.iban}
                  onChange={(v) => setDodPatch({ iban: v })}
                  placeholder="CZ65 0800 0000 1920 0014 5399"
                  onRemove={() => hide('s04.iban')}
                />
              )}
              {!isHidden('s04.bic') && (
                <DocRow
                  k="BIC / SWIFT"
                  mono
                  value={dod.bic}
                  onChange={(v) => setDodPatch({ bic: v })}
                  placeholder="např. GIBACZPX"
                  onRemove={() => hide('s04.bic')}
                />
              )}
              {!isHidden('s04.ss') && (
                <DocRow
                  k="Specifický symbol"
                  mono
                  value={meta.ss}
                  onChange={(v) => setMetaPatch({ ss: v })}
                  placeholder="(volitelné)"
                  onRemove={() => hide('s04.ss')}
                />
              )}
              <DocCustomRows rows={rows} section="s04" />
              {ibanError && (
                <DocRow
                  k="⚠ Chyba"
                  v={<span style={{ color: 'var(--sd-danger, #c00)' }}>{ibanError}</span>}
                />
              )}
              <DocRow
                k="QR platba"
                v={
                  <span style={qrWrap}>
                    {spd ? (
                      <QRCodeSVG value={spd} size={120} level="M" bgColor="#ffffff" fgColor="#17181b" />
                    ) : (
                      <span className="docNoPrint" style={{ fontSize: 11.5, color: 'var(--sd-doc-dim)' }}>
                        Zadejte IBAN pro vygenerování QR platby (SPD).
                      </span>
                    )}
                    {spd && (
                      <span style={qrInfoCol}>
                        <span style={qrLabel}>K ÚHRADĚ</span>
                        <strong style={{ fontFamily: 'var(--sd-font-mono)', fontSize: 14 }}>
                          {fmtKc(total)}
                        </strong>
                        <span style={{ fontSize: 11, color: 'var(--sd-doc-dim)' }}>
                          VS {meta.vs} · splatnost {meta.splatnost}
                        </span>
                      </span>
                    )}
                  </span>
                }
              />
            </DocBlock>

            <DocBlock
              idx="05"
              title="Odběratel"
              hiddenCount={hiddenCount('s05')}
              onRestoreHidden={() => restoreSection('s05')}
              actions={
                <>
                  <DocAddRowButton rows={rows} section="s05" />
                  <CompanyPickerButton
                    preferRole="objednatel"
                    title="Načíst odběratele z adresáře"
                    onPick={(c: CompanyPickResult) =>
                      setObj({
                        nazev: c.nazev,
                        sidlo: c.adresa,
                        ico: c.ico,
                        dic: c.dic,
                        tel: c.kontakt_tel,
                        email: c.kontakt_email,
                      })
                    }
                  />
                  <SaveCompanyButton
                    title="Uložit odběratele do adresáře"
                    build={() => ({
                      role: 'objednatel',
                      nazev: obj.nazev,
                      ico: obj.ico,
                      dic: obj.dic,
                      adresa: obj.sidlo,
                      kontakt_tel: obj.tel,
                      kontakt_email: obj.email,
                    })}
                  />
                </>
              }
            >
              {!isHidden('s05.nazev') && (
                <DocRow
                  k="Název"
                  v={
                    <DocInlineAresName
                      value={obj.nazev}
                      placeholder="např. Petr Svoboda"
                      onChange={(v) => setObjPatch({ nazev: v })}
                      onPick={(d: AresPickResult) =>
                        setObjPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })
                      }
                    />
                  }
                  onRemove={() => hide('s05.nazev')}
                />
              )}
              {!isHidden('s05.sidlo') && (
                <DocRow
                  k="Sídlo"
                  value={obj.sidlo}
                  onChange={(v) => setObjPatch({ sidlo: v })}
                  placeholder="ulice č.p., PSČ město"
                  actions={
                    <MapPickerButton
                      value={obj.sidlo}
                      onApply={(v) => setObjPatch({ sidlo: v })}
                      title="Sídlo odběratele"
                      label="Mapa"
                    />
                  }
                  onRemove={() => hide('s05.sidlo')}
                />
              )}
              {!isHidden('s05.ico') && (
                <DocRow
                  k="IČ"
                  mono
                  value={obj.ico}
                  onChange={(v) => setObjPatch({ ico: v })}
                  placeholder="12345678"
                  actions={
                    <DocInlineAresLookup
                      ico={obj.ico}
                      onResult={(d) => setObjPatch({ nazev: d.nazev, ico: d.ico, sidlo: d.adresa })}
                    />
                  }
                  onRemove={() => hide('s05.ico')}
                />
              )}
              {!isHidden('s05.dic') && (
                <DocRow
                  k="DIČ"
                  mono
                  value={obj.dic}
                  onChange={(v) => setObjPatch({ dic: v })}
                  placeholder="CZ12345678"
                  onRemove={() => hide('s05.dic')}
                />
              )}
              {!isHidden('s05.tel') && (
                <DocRow
                  k="Telefon"
                  mono
                  value={obj.tel}
                  onChange={(v) => setObjPatch({ tel: v })}
                  placeholder="+420 …"
                  inputType="tel"
                  onRemove={() => hide('s05.tel')}
                />
              )}
              {!isHidden('s05.email') && (
                <DocRow
                  k="E-mail"
                  mono
                  value={obj.email}
                  onChange={(v) => setObjPatch({ email: v })}
                  placeholder="email@…"
                  inputType="email"
                  actions={
                    <DocContactQR
                      name={obj.nazev}
                      role="Odběratel"
                      phone={obj.tel}
                      email={obj.email}
                      title="QR vizitka — odběratel"
                    />
                  }
                  onRemove={() => hide('s05.email')}
                />
              )}
              <DocCustomRows rows={rows} section="s05" />
            </DocBlock>

            <DocBlock
              idx="06"
              title="Fakturované plnění"
              actions={
                <DocDphSelect value={dphPct} onChange={setDphPct} />
              }
            >
              <DocItemsTable items={items} onChange={setItems} title="Položky" />
              <DocRow
                k="Mezisoučet"
                mono
                v={<span style={{ fontFamily: 'var(--sd-font-mono)' }}>{fmtKc(subtotal)}</span>}
              />
              <DocRow
                k={`DPH ${dphPct} %`}
                mono
                v={<span style={{ fontFamily: 'var(--sd-font-mono)' }}>{fmtKc(vat)}</span>}
              />
              <DocRow
                k="K ÚHRADĚ"
                mono
                v={
                  <strong style={{ fontFamily: 'var(--sd-font-mono)', fontSize: 14 }}>
                    {fmtKc(total)}
                  </strong>
                }
              />
            </DocBlock>

            <DocBlock
              idx="07"
              title="Poznámky"
              actions={
                <DocActionButton onClick={addPoznamka} title="Přidat poznámku">
                  + Poznámka
                </DocActionButton>
              }
            >
              {poznamky.map((p, i) => (
                <DocRow
                  key={p.id}
                  k={`Poznámka ${i + 1}`}
                  multiline
                  value={p.text}
                  onChange={(v) => updatePoznamka(p.id, v)}
                  placeholder="text poznámky…"
                  actions={
                    <DocActionButton onClick={() => removePoznamka(p.id)} title="Odebrat poznámku">
                      ✕
                    </DocActionButton>
                  }
                />
              ))}
              <DocRow
                k="Doložka"
                v={
                  <span style={{ fontSize: 11.5, color: 'var(--sd-doc-dim)' }}>
                    Dokument je vystaven v souladu se zákonem č. 235/2004 Sb. o DPH.
                  </span>
                }
              />
            </DocBlock>

            <DocBlock
              idx="08"
              title="Podpisy"
              actions={
                <DocActionButton onClick={addExtra} title="Přidat dalšího účastníka">
                  + Účastník
                </DocActionButton>
              }
            >
              <DocRow
                k="Vystavil — jméno"
                value={podpisy.vystavil.jmeno}
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, vystavil: { ...p.vystavil, jmeno: v } }))
                }
              />
              <DocRow
                k="Vystavil — funkce"
                value={podpisy.vystavil.funkce}
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, vystavil: { ...p.vystavil, funkce: v } }))
                }
                placeholder="např. jednatel"
              />
              <DocRow
                k="Převzal — jméno"
                value={podpisy.prevzal.jmeno}
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, prevzal: { ...p.prevzal, jmeno: v } }))
                }
              />
              <DocRow
                k="Převzal — funkce"
                value={podpisy.prevzal.funkce}
                onChange={(v) =>
                  setPodpisy((p) => ({ ...p, prevzal: { ...p.prevzal, funkce: v } }))
                }
              />
              {podpisy.extra.map((e, i) => (
                <Fragment key={e.id}>
                  <DocRow
                    k={`Účastník ${i + 1} — strana`}
                    value={e.strana}
                    onChange={(v) => updateExtra(e.id, { strana: v })}
                    actions={
                      <DocActionButton onClick={() => removeExtra(e.id)} title="Odebrat účastníka">
                        ✕
                      </DocActionButton>
                    }
                  />
                  <DocRow
                    k="Jméno"
                    value={e.jmeno}
                    onChange={(v) => updateExtra(e.id, { jmeno: v })}
                  />
                  <DocRow
                    k="Funkce"
                    value={e.funkce}
                    onChange={(v) => updateExtra(e.id, { funkce: v })}
                  />
                </Fragment>
              ))}
            </DocBlock>

            <DocSignatures
              items={[
                {
                  label: 'Vystavil',
                  name: podpisy.vystavil.jmeno || '—',
                  role: podpisy.vystavil.funkce || '',
                  image: podpisy.vystavil.image,
                  onSign: (v) =>
                    setPodpisy((p) => ({ ...p, vystavil: { ...p.vystavil, image: v } })),
                },
                {
                  label: 'Převzal',
                  name: podpisy.prevzal.jmeno || '—',
                  role: podpisy.prevzal.funkce || '',
                  image: podpisy.prevzal.image,
                  onSign: (v) =>
                    setPodpisy((p) => ({ ...p, prevzal: { ...p.prevzal, image: v } })),
                },
                ...podpisy.extra.map((e) => ({
                  label: e.strana || 'Účastník',
                  name: e.jmeno || '—',
                  role: e.funkce,
                  image: e.image,
                  onSign: (v: string) => updateExtra(e.id, { image: v }),
                })),
              ]}
            />
          </DocPage>
        </div>
      </DocLayout>

      <SendEmailModal
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        buildHtml={() => buildDocHtml(previewRef.current)}
        {...(withBundle && bundle.merged
          ? {
              buildAttachment: async () => {
                const { blob, filename } = await bundle.mergedPdf(
                  await buildDocHtml(previewRef.current),
                  docTitle,
                );
                return { base64: await blobToBase64(blob), filename };
              },
            }
          : withBundle
            ? { extraAttachments: bundle.extraAttachments }
            : {})}
        attachmentsNote={
          withBundle
            ? `${bundle.merged ? 'Sloučeno do jednoho PDF' : 'Přiloženo zvlášť'}: ${bundle.chosen.map((d) => d.title).join(', ')}`
            : undefined
        }
        docType="faktura"
        docTitle={docTitle}
        defaultTo={obj.email}
        defaultCc={dod.email}
        defaultSubject={
          meta.cislo
            ? `Faktura ${meta.cislo} — ${obj.nazev || 'odběratel'}`
            : `Faktura — ${obj.nazev || 'odběratel'}`
        }
        meta={`Odběratel: ${obj.nazev || '—'}${activeStavba?.nazev ? ' · ' + activeStavba.nazev : ''}`}
      />
    </>
  );
}

const qrWrap: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 14,
  padding: '6px 0',
};
const qrInfoCol: CSSProperties = {
  display: 'inline-flex',
  flexDirection: 'column',
  gap: 2,
};
const qrLabel: CSSProperties = {
  fontSize: 10.5,
  fontWeight: 700,
  color: 'var(--sd-doc-dim)',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
};
